import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../components/common/button/Button';
import styles from './InvoiceEmptyDetail.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import reportService from '../../services/reportService';
import companyService from '../../services/companyService';
import { InvoiceEmptyDetailedReport } from '../../types/reportTypes';
import { Company } from '../../types/companyTypes';

const InvoiceEmptyDetail: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [detailData, setDetailData] = useState<InvoiceEmptyDetailedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<Company | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Get date range from URL params
  const startDate = searchParams.get('startDate') || undefined;
  const endDate = searchParams.get('endDate') || undefined;

  const fetchCompany = async () => {
    try {
      const response = await companyService.getCompany('1');
      if (response && typeof response === 'object') {
        const normalizedCompany: Company = {
          id: response.id ?? '',
          name: response.name ?? '',
          email: response.email ?? '',
          phone: response.phone ?? '',
          address: response.address ?? '',
          city: response.city ?? '',
          state: response.state ?? '',
          zip_code: response.zip_code ?? '',
          country: response.country ?? '',
          website: response.website ?? '',
          gst: response.gst ?? '',
          is_active: response.is_active ?? true,
          created_at: response.created_at ?? new Date().toISOString(),
          updated_at: response.updated_at ?? new Date().toISOString(),
        };
        setCompany(normalizedCompany);
      }
    } catch (err) {
      console.error('Error fetching company:', err);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  useEffect(() => {
    const loadDetailData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await reportService.getInvoiceEmptyDetailedReport({
          customerId: id,
          startDate,
          endDate,
        });

        // Find the specific customer from the response
        const customerData = response.data?.find(
          (item) => Number(item.customer_id) === Number(id)
        );

        if (customerData) {
          setDetailData(customerData);
        } else {
          toast.error('Customer data not found');
        }
      } catch (error) {
        console.error('Failed to load invoice empty detail:', error);
        toast.error('Failed to load invoice empty detail');
      } finally {
        setLoading(false);
      }
    };

    loadDetailData();
  }, [id, startDate, endDate]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getPrintContent = () => {
    if (!detailData) return '';

    const now = new Date();
    const printDate = now.toLocaleDateString();
    const printTime = now.toLocaleTimeString();

    const companyName = company?.name || 'Company Name';
    const companyAddressParts = [
      company?.address,
      company?.city,
      company?.state,
      company?.zip_code,
      company?.country,
    ].filter(Boolean);
    const companyAddress = companyAddressParts.join(', ');

    const dateRange = startDate && endDate
      ? `${formatDate(startDate)} to ${formatDate(endDate)}`
      : 'All Time';

    return `
      <div style="text-align:center; margin-bottom:16px;">
        <h1 style="margin-bottom:0; font-size:1.4rem; color:green;">
          ${companyName}
        </h1>
        ${companyAddress ? `<div style="font-size:1rem; margin-bottom:4px;">${companyAddress}</div>` : ''}
        ${company?.phone ? `<div>Contact: ${company.phone}</div>` : ''}
        ${company?.email ? `<div>Email: ${company.email}</div>` : ''}
        ${company?.gst ? `<div>GST: ${company.gst}</div>` : ''}
        <div style="margin-top:4px; font-size:0.9rem;">Printed on ${printDate} - ${printTime}</div>
      </div>
      
      <h2 style="text-align:center; margin-bottom:16px;">Empty Cylinder Collection Report</h2>
      
      <div style="display:flex; justify-content:space-between; margin-bottom:16px; border:1px solid #ccc; padding:12px; border-radius:8px;">
        <div>
          <strong>Customer Name:</strong> ${detailData.customer_name}<br/>
          <strong>Phone:</strong> ${detailData.phone}<br/>
          ${detailData.email ? `<strong>Email:</strong> ${detailData.email}<br/>` : ''}
          ${detailData.address ? `<strong>Address:</strong> ${detailData.address}` : ''}
        </div>
        <div style="text-align:right;">
          <strong>Date Range:</strong> ${dateRange}<br/>
          ${detailData.gst ? `<strong>GST:</strong> ${detailData.gst}` : ''}
        </div>
      </div>

      <h3 style="margin-top:20px; margin-bottom:10px;">Date-wise Invoice Details</h3>
      <table style="width:100%; border-collapse:collapse; margin-bottom:16px;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">S.No</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">Date</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">Invoice No.</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">Product</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Qty Provided</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Empty Returned</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Balance</th>
          </tr>
        </thead>
        <tbody>
          ${detailData.invoices.map((invoice, idx) => {
            const invoiceRow = `
              <tr style="background:#f0f8ff;">
                <td style="border:1px solid #ccc; padding:8px; font-weight:bold;">${idx + 1}</td>
                <td style="border:1px solid #ccc; padding:8px; font-weight:bold;">${formatDate(invoice.invoice_date)}</td>
                <td style="border:1px solid #ccc; padding:8px; font-weight:bold;">${invoice.invoice_number}</td>
                <td style="border:1px solid #ccc; padding:8px;"></td>
                <td style="border:1px solid #ccc; padding:8px; text-align:center; font-weight:bold;">${invoice.invoice_qty_provided}</td>
                <td style="border:1px solid #ccc; padding:8px; text-align:center; font-weight:bold;">${invoice.invoice_empty_returned}</td>
                <td style="border:1px solid #ccc; padding:8px; text-align:center; font-weight:bold; color:${invoice.invoice_balance_qty > 0 ? '#dc2626' : '#059669'};">${invoice.invoice_balance_qty}</td>
              </tr>
            `;
            
            const itemRows = invoice.items.map(item => `
              <tr style="background:#f9fafb;">
                <td style="border:1px solid #ccc; padding:6px;"></td>
                <td style="border:1px solid #ccc; padding:6px;"></td>
                <td style="border:1px solid #ccc; padding:6px;"></td>
                <td style="border:1px solid #ccc; padding:6px; padding-left:20px;">• ${item.product_name}</td>
                <td style="border:1px solid #ccc; padding:6px; text-align:center;">${item.quantity}</td>
                <td style="border:1px solid #ccc; padding:6px; text-align:center;">${item.empty_returned}</td>
                <td style="border:1px solid #ccc; padding:6px; text-align:center; color:${item.balance_qty > 0 ? '#dc2626' : '#059669'};">${item.balance_qty}</td>
              </tr>
            `).join('');
            
            return invoiceRow + itemRows;
          }).join('')}
          <tr style="background:#e5e7eb; font-weight:bold;">
            <td colspan="4" style="border:1px solid #ccc; padding:10px; text-align:right;">Total</td>
            <td style="border:1px solid #ccc; padding:10px; text-align:center;">${detailData.total_qty_provided}</td>
            <td style="border:1px solid #ccc; padding:10px; text-align:center;">${detailData.total_empty_returned}</td>
            <td style="border:1px solid #ccc; padding:10px; text-align:center; color:${detailData.total_balance_qty > 0 ? '#dc2626' : '#059669'};">${detailData.total_balance_qty}</td>
          </tr>
        </tbody>
      </table>

      <h3 style="margin-top:20px; margin-bottom:10px;">Product-wise Summary</h3>
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">Product Name</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Qty Provided</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Empty Returned</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Balance</th>
          </tr>
        </thead>
        <tbody>
          ${detailData.products.map(product => `
            <tr>
              <td style="border:1px solid #ccc; padding:8px;">${product.product_name}</td>
              <td style="border:1px solid #ccc; padding:8px; text-align:center;">${product.qty_provided}</td>
              <td style="border:1px solid #ccc; padding:8px; text-align:center;">${product.empty_returned}</td>
              <td style="border:1px solid #ccc; padding:8px; text-align:center; font-weight:bold; color:${product.balance_qty > 0 ? '#dc2626' : '#059669'};">${product.balance_qty}</td>
            </tr>
          `).join('')}
          <tr style="background:#e5e7eb; font-weight:bold;">
            <td style="border:1px solid #ccc; padding:10px; text-align:right;">Total</td>
            <td style="border:1px solid #ccc; padding:10px; text-align:center;">${detailData.total_qty_provided}</td>
            <td style="border:1px solid #ccc; padding:10px; text-align:center;">${detailData.total_empty_returned}</td>
            <td style="border:1px solid #ccc; padding:10px; text-align:center; color:${detailData.total_balance_qty > 0 ? '#dc2626' : '#059669'};">${detailData.total_balance_qty}</td>
          </tr>
        </tbody>
      </table>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=800,width=1100');
    if (printWindow) {
      printWindow.document.write(`<html><head><title>${detailData?.customer_name} - Empty Cylinder Report</title>`);
      printWindow.document.write(`
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; }
          th { background: #f5f5f5; }
          @media print {
            body { margin: 10px; }
            @page { margin: 0.5cm; size: auto; }
          }
        </style>
      `);
      printWindow.document.write('</head><body>');
      printWindow.document.write(getPrintContent());
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  const exportToCSV = () => {
    if (!detailData) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Date', 'Invoice No.', 'Product', 'Qty Provided', 'Empty Returned', 'Balance'];

    const rows: string[][] = [];
    
    detailData.invoices.forEach((invoice) => {
      invoice.items.forEach((item) => {
        rows.push([
          formatDate(invoice.invoice_date),
          invoice.invoice_number,
          item.product_name,
          item.quantity.toString(),
          item.empty_returned.toString(),
          item.balance_qty.toString(),
        ]);
      });
    });

    // Add totals row
    rows.push([
      '',
      '',
      'Total',
      detailData.total_qty_provided.toString(),
      detailData.total_empty_returned.toString(),
      detailData.total_balance_qty.toString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => {
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const filename = `Empty_Cylinder_Report_${detailData.customer_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading empty cylinder details...</p>
      </div>
    );
  }

  if (!detailData) {
    return (
      <div className={styles.errorContainer}>
        <h2>Data Not Found</h2>
        <p>Unable to load empty cylinder information for this customer.</p>
        <Button onClick={() => navigate('/reports')}>Back to Reports</Button>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={contentRef}>
      {/* Print Header - Hidden on screen, visible on print */}
      <div className={styles.printHeader}>
        <h2>{detailData.customer_name}</h2>
        <p>Balance Cylinders: {detailData.total_balance_qty}</p>
      </div>

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2>{detailData.customer_name} - Empty Cylinder Report</h2>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={handlePrint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </Button>
          <Button variant="primary" onClick={exportToCSV}>
            📥 Export to CSV
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Customer Info Section */}
        <div className={styles.section}>
          <h3>Customer Information</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Customer Name</label>
              <span>{detailData.customer_name}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Phone Number</label>
              <span>{detailData.phone}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Email Address</label>
              <span>{detailData.email || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Address</label>
              <span>{detailData.address || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>GST Number</label>
              <span>{detailData.gst || '-'}</span>
            </div>
            {startDate && endDate && (
              <div className={styles.infoItem}>
                <label>Date Range</label>
                <span>{formatDate(startDate)} to {formatDate(endDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Summary Section */}
        <div className={styles.section}>
          <h3>Summary</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Total Invoices</label>
              <span>{detailData.invoices.length}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Total Qty Provided</label>
              <span>{detailData.total_qty_provided}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Total Empty Returned</label>
              <span>{detailData.total_empty_returned}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Balance Cylinders</label>
              <span className={detailData.total_balance_qty > 0 ? styles.balanceOwed : styles.balancePaid}>
                {detailData.total_balance_qty}
              </span>
            </div>
          </div>
        </div>

        {/* Date-wise Invoice Details Section */}
        <div className={styles.section}>
          <h3>Date-wise Invoice Details</h3>
          {detailData.invoices.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No invoices found.</p>
            </div>
          ) : (
            <div className={styles.invoiceTable}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice No.</th>
                    <th>Product</th>
                    <th>Qty Provided</th>
                    <th>Empty Returned</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.invoices.map((invoice) => (
                    <React.Fragment key={invoice.invoice_id}>
                      {/* Invoice Header Row */}
                      <tr className={styles.invoiceRow}>
                        <td><strong>{formatDate(invoice.invoice_date)}</strong></td>
                        <td>
                          <span className={styles.invoiceBadge}>
                            {invoice.invoice_number}
                          </span>
                        </td>
                        <td></td>
                        <td className={styles.qtyCell}><strong>{invoice.invoice_qty_provided}</strong></td>
                        <td className={styles.returnedCell}><strong>{invoice.invoice_empty_returned}</strong></td>
                        <td className={styles.balanceCell}>
                          <strong style={{ color: invoice.invoice_balance_qty > 0 ? '#dc2626' : '#059669' }}>
                            {invoice.invoice_balance_qty}
                          </strong>
                        </td>
                      </tr>
                      {/* Invoice Items */}
                      {invoice.items.map((item, idx) => (
                        <tr key={`${invoice.invoice_id}-${idx}`} className={styles.itemRow}>
                          <td></td>
                          <td></td>
                          <td style={{ paddingLeft: '24px' }}>• {item.product_name}</td>
                          <td className={styles.qtyCell}>{item.quantity}</td>
                          <td className={styles.returnedCell}>{item.empty_returned}</td>
                          <td className={styles.balanceCell} style={{ color: item.balance_qty > 0 ? '#dc2626' : '#059669' }}>
                            {item.balance_qty}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {/* Total Row */}
                  <tr className={styles.totalRow}>
                    <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                    <td className={styles.qtyCell}><strong>{detailData.total_qty_provided}</strong></td>
                    <td className={styles.returnedCell}><strong>{detailData.total_empty_returned}</strong></td>
                    <td className={styles.balanceCell}>
                      <strong style={{ color: detailData.total_balance_qty > 0 ? '#dc2626' : '#059669' }}>
                        {detailData.total_balance_qty}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Product-wise Summary Section */}
        <div className={styles.section}>
          <h3>Product-wise Summary</h3>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Product Name</th>
                  <th>Qty Provided</th>
                  <th>Empty Returned</th>
                  <th>Balance Qty</th>
                </tr>
              </thead>
              <tbody>
                {detailData.products.length > 0 ? (
                  <>
                    {detailData.products.map((product, index) => (
                      <tr key={product.product_id}>
                        <td>{index + 1}</td>
                        <td>{product.product_name}</td>
                        <td>{product.qty_provided}</td>
                        <td>{product.empty_returned}</td>
                        <td style={{
                          color: product.balance_qty > 0 ? '#dc2626' : '#059669',
                          fontWeight: 600,
                        }}>
                          {product.balance_qty}
                        </td>
                      </tr>
                    ))}
                    <tr className={styles.totalRow}>
                      <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                      <td style={{ fontWeight: 'bold' }}>{detailData.total_qty_provided}</td>
                      <td style={{ fontWeight: 'bold' }}>{detailData.total_empty_returned}</td>
                      <td style={{
                        fontWeight: 'bold',
                        color: detailData.total_balance_qty > 0 ? '#dc2626' : '#059669',
                      }}>
                        {detailData.total_balance_qty}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>
                      No product data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default InvoiceEmptyDetail;